import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Typography, TextField, Button, Grid, Alert,
  CircularProgress, Box, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip, Divider,
  Autocomplete
} from '@mui/material';
import {
  Add, Edit, Delete, ExpandMore, MenuBook, Warning
} from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';

const API_BASE = API_BASE_URL;
const USC_GREEN = '#046A38';

const AdminSOPPage = () => {
  const { user } = useAuth();

  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating
  const [form, setForm] = useState({ title: '', category: '', content: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${user?.idToken}` } };

  useEffect(() => {
    if (user?.idToken) loadSops();
  }, [user?.idToken]);

  const loadSops = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/admin/sops`, authHeader);
      setSops(res.data.sops || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ title: '', category: '', content: '' });
    setDialogOpen(true);
  };

  const openEdit = (sop) => {
    setEditingId(sop.id);
    setForm({ title: sop.title || '', category: sop.category || '', content: sop.content || '' });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ title: '', category: '', content: '' });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category.trim() || 'General',
        content: form.content.trim(),
      };
      if (editingId) {
        await axios.put(`${API_BASE}/admin/sops/${editingId}`, payload, authHeader);
        setSuccess('Guide updated');
      } else {
        await axios.post(`${API_BASE}/admin/sops`, payload, authHeader);
        setSuccess('Guide added');
      }
      closeDialog();
      await loadSops();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save guide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${API_BASE}/admin/sops/${deleteTarget.id}`, authHeader);
      setSuccess('Guide deleted');
      setDeleteTarget(null);
      await loadSops();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete guide');
    } finally {
      setDeleting(false);
    }
  };

  // Group SOPs by category for display
  const grouped = sops.reduce((acc, sop) => {
    const cat = sop.category || 'General';
    (acc[cat] = acc[cat] || []).push(sop);
    return acc;
  }, {});
  const categories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  // Existing categories for the Autocomplete suggestions
  const existingCategories = [...new Set(sops.map((s) => s.category || 'General'))].sort();

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: USC_GREEN, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBook fontSize="large" /> SOP Guides
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}
          sx={{ bgcolor: USC_GREEN, '&:hover': { bgcolor: '#035028' } }}>
          Add Guide
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Step-by-step instructions for common tasks on this site. When you do something, write it
        up here so anyone can replicate it.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : sops.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <MenuBook sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No guides yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Click “Add Guide” to write the first step-by-step guide.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        categories.map((cat) => (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ fontWeight: 'bold', color: USC_GREEN }}>
              {cat}
            </Typography>
            {grouped[cat].map((sop) => (
              <Accordion key={sop.id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 600 }}>{sop.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                  >
                    {sop.content}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {sop.lastEditedBy || sop.author
                        ? `Last edited by ${sop.lastEditedBy || sop.author}`
                        : ''}
                      {sop.updatedAt ? ` · ${formatDate(sop.updatedAt)}` : ''}
                    </Typography>
                    <Box>
                      <Button size="small" startIcon={<Edit />} onClick={() => openEdit(sop)}>
                        Edit
                      </Button>
                      <Button size="small" color="error" startIcon={<Delete />} onClick={() => setDeleteTarget(sop)}>
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Guide' : 'Add Guide'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth label="Title"
                placeholder="e.g. How to approve an alumni registration"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                freeSolo
                options={existingCategories}
                value={form.category}
                onChange={(e, val) => setForm({ ...form, category: val || '' })}
                onInputChange={(e, val) => setForm({ ...form, category: val })}
                renderInput={(params) => (
                  <TextField {...params} label="Category" placeholder="e.g. Approvals" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Steps / Instructions"
                placeholder={"Write the steps, one per line. For example:\n1. Sign in as an Admin.\n2. Open the Admin menu and click Alumni Requests.\n3. Review the details, then click Approve."}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                multiline minRows={10}
                required
              />
              <Typography variant="caption" color="text.secondary">
                Line breaks are preserved. Number your steps for clarity.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: USC_GREEN, '&:hover': { bgcolor: '#035028' } }}
          >
            {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Guide')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" /> Delete Guide
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} startIcon={<Delete />}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSOPPage;
